import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchCollectionList, getUserAssetsV2, type UserAssetV2Record } from "@apis/http/api";
import {
  PathsApiUserAssetsGetParametersQueryAssetSource,
  PathsApiUserAssetsGetParametersQueryAssetType,
  PathsApiUserAssetsGetParametersQueryState,
  UserCollectionRequestType,
} from "@apis/generated/openapi";
import { useScrollLoad, type ScrollPageResult } from "@hooks/useScrollLoad";
import { getAssetCoverUrl } from "@pages/library/hooks/useGeneratedVideoAssets";
import type { LibrarySubTab } from "@pages/videoRemake/ReferenceVideoLibraryPanel";
import type { ReferenceVideoLibraryItem } from "@pages/videoRemake/ReferenceVideoCard";

const PAGE_SIZE = 20;

interface ScrollState {
  scrollTop: number;
}

interface SavedState {
  items: ReferenceVideoLibraryItem[];
  loading: boolean;
  error: Error | null;
  initialized: boolean;
}

export interface ReferenceVideoLibraryTabState {
  videos: ReferenceVideoLibraryItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  scrollTop: number;
  sentinelRef: (node: HTMLElement | null) => void;
}

export interface UseReferenceVideoLibraryOptions {
  enabled: boolean;
  subTab: LibrarySubTab;
}

export interface UseReferenceVideoLibraryResult extends ReferenceVideoLibraryTabState {
  saveScrollTop: (tab: LibrarySubTab, scrollTop: number) => void;
  refresh: () => void;
}

const getRecordContext = (record: UserAssetV2Record): Record<string, unknown> => {
  const context = record.jobItem?.context;
  return context && typeof context === "object" ? (context as Record<string, unknown>) : {};
};

const readString = (source: Record<string, unknown>, key: string): string | undefined => {
  const value = source[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const normalizeAssetRecord = (
  record: UserAssetV2Record,
  variant: "generated" | "uploaded",
): ReferenceVideoLibraryItem => {
  const context = getRecordContext(record);
  const videoUrl = record.url ?? readString(context, "videoUrl");
  const coverUrl =
    record.coverImageUrl ?? getAssetCoverUrl(record) ?? (variant === "generated" ? record.url : undefined);
  const productImage =
    readString(context, "productImageUrl") ??
    readString(context, "productCoverImageUrl") ??
    readString(context, "coverImageUrl");

  return {
    id: String(record.id ?? record.jobItem?.jobId ?? record.url ?? record.fileName ?? `${variant}-unknown`),
    variant,
    coverUrl,
    videoUrl,
    productImage,
    productTitle: readString(context, "productTitle") ?? record.assetDescription ?? record.fileName,
    raw: record,
  };
};

const normalizeSavedItem = (record: unknown, index: number): ReferenceVideoLibraryItem => {
  const item = record && typeof record === "object" ? (record as Record<string, unknown>) : {};
  const id = item.id ?? item.collectionId ?? item.videoId ?? item.assetId ?? index;
  const coverUrl =
    readString(item, "coverImageUrl") ??
    readString(item, "coverUrl") ??
    readString(item, "thumbnailUrl") ??
    readString(item, "videoCoverUrl");
  const productImage =
    readString(item, "productImageUrl") ??
    readString(item, "productCoverImageUrl") ??
    readString(item, "productImage") ??
    readString(item, "productCoverUrl");
  const productTitle = readString(item, "productTitle") ?? readString(item, "productName");
  // variant 由商品信息存在与否推断（后端暂无明确 type 字段）：
  // 含 productTitle/productImage 视为带商品的生成视频（"generated"），否则视为纯收藏视频（"saved"）。
  // 若后端后续补充 type 字段，应优先使用。
  const variant = productTitle || productImage ? "generated" : "saved";

  return {
    id: String(id),
    variant,
    coverUrl,
    videoUrl: readString(item, "videoUrl") ?? readString(item, "url"),
    productImage,
    productTitle: productTitle ?? readString(item, "description"),
    raw: record,
  };
};

const createAssetFetcher =
  (assetSource: PathsApiUserAssetsGetParametersQueryAssetSource, variant: "generated" | "uploaded") =>
  async ({
    pageNo,
    pageSize,
  }: {
    pageNo: number;
    pageSize: number;
  }): Promise<ScrollPageResult<ReferenceVideoLibraryItem>> => {
    const response = await getUserAssetsV2({
      assetType: PathsApiUserAssetsGetParametersQueryAssetType.VID,
      assetSource,
      pageNo,
      pageSize,
      state:
        assetSource === PathsApiUserAssetsGetParametersQueryAssetSource.AI
          ? PathsApiUserAssetsGetParametersQueryState.COMPLETED
          : undefined,
    });

    const data = (response?.data ?? [])
      .filter((record) => record.state === 1)
      .map((record) => normalizeAssetRecord(record, variant));

    return {
      data,
      pageNo: Number(response?.pageNo ?? pageNo),
      pageSize: Number(response?.pageSize ?? pageSize),
      total: Number(response?.total ?? data.length),
    };
  };

const noopSentinelRef = () => {};

export const useReferenceVideoLibrary = ({
  enabled,
  subTab,
}: UseReferenceVideoLibraryOptions): UseReferenceVideoLibraryResult => {
  const generated = useScrollLoad<ReferenceVideoLibraryItem, Record<string, never>>({
    enabled,
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: createAssetFetcher(PathsApiUserAssetsGetParametersQueryAssetSource.AI, "generated"),
  });
  const uploaded = useScrollLoad<ReferenceVideoLibraryItem, Record<string, never>>({
    enabled,
    params: {},
    pageSize: PAGE_SIZE,
    fetcher: createAssetFetcher(PathsApiUserAssetsGetParametersQueryAssetSource.USER, "uploaded"),
  });

  const [saved, setSaved] = useState<SavedState>({
    items: [],
    loading: false,
    error: null,
    initialized: false,
  });
  const requestSeqRef = useRef(0);
  const scrollStateRef = useRef<Record<LibrarySubTab, ScrollState>>({
    generated: { scrollTop: 0 },
    uploaded: { scrollTop: 0 },
    saved: { scrollTop: 0 },
  });

  const loadSaved = useCallback(async () => {
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setSaved((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await fetchCollectionList({ type: UserCollectionRequestType.Value1 });
      if (requestSeqRef.current !== seq) return;
      const list = Array.isArray(response) ? response : [];
      setSaved({
        items: list.map(normalizeSavedItem),
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      if (requestSeqRef.current !== seq) return;
      setSaved((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error : new Error(String(error)),
      }));
    }
  }, []);

  useEffect(() => {
    if (!enabled || subTab !== "saved" || saved.initialized || saved.loading) return;
    void loadSaved();
  }, [enabled, loadSaved, saved.initialized, saved.loading, subTab]);

  const saveScrollTop = useCallback((tab: LibrarySubTab, scrollTop: number) => {
    scrollStateRef.current[tab] = { scrollTop };
  }, []);

  const refresh = useCallback(() => {
    if (subTab === "generated") {
      generated.refresh();
      return;
    }
    if (subTab === "uploaded") {
      uploaded.refresh();
      return;
    }
    void loadSaved();
  }, [generated, loadSaved, subTab, uploaded]);

  return useMemo(() => {
    const scrollTop = scrollStateRef.current[subTab].scrollTop;
    if (subTab === "generated") {
      return {
        videos: generated.items,
        loading: generated.loading,
        loadingMore: generated.loadingMore,
        hasMore: generated.hasMore,
        error: generated.error,
        scrollTop,
        sentinelRef: generated.sentinelRef,
        saveScrollTop,
        refresh,
      };
    }
    if (subTab === "uploaded") {
      return {
        videos: uploaded.items,
        loading: uploaded.loading,
        loadingMore: uploaded.loadingMore,
        hasMore: uploaded.hasMore,
        error: uploaded.error,
        scrollTop,
        sentinelRef: uploaded.sentinelRef,
        saveScrollTop,
        refresh,
      };
    }
    return {
      videos: saved.items,
      loading: saved.loading,
      loadingMore: false,
      hasMore: false,
      error: saved.error,
      scrollTop,
      sentinelRef: noopSentinelRef,
      saveScrollTop,
      refresh,
    };
  }, [generated, refresh, saveScrollTop, saved, subTab, uploaded]);
};
